/**
 * Time writes shared by the timesheet, Home cards and the case pane: call the
 * endpoint, refresh every time view, and say what happened with an Undo when
 * there is one. Errors toast and resolve to null so callers stay simple.
 */
import { toast } from "sonner";

import {
  type EntryPatch,
  ExpenseAPI,
  GuidelineAPI,
  type NewEntry,
  PrebillAPI,
  TimeAdminAPI,
  TimeAPI
} from "./api/timeApi";
import { flushDrafts } from "./narrativeDrafts";
import { notifyTimeChanged } from "./timeEvents";
import type {
  AppliedRule,
  BillingRule,
  ExportBatch,
  EntryFlag,
  ExportRequest,
  GuidelineKind,
  GuidelineOwner,
  NewExpense,
  PrebillMatter,
  TimeGuideline,
  TimeEntry,
  TimeMatter,
  Timekeeper,
  WaitingRow
} from "./types";
import { formatAmount, formatHours, totalHours } from "./utils";

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const attempt = async <T>(run: () => Promise<T>): Promise<T | null> => {
  try {
    const out = await run();
    notifyTimeChanged();
    return out;
  } catch (error) {
    toast.error(
      error instanceof Error ? error.message : "Couldn't update your time"
    );
    notifyTimeChanged();
    return null;
  }
};

const undoable = (message: string, undo: () => Promise<unknown>) =>
  toast(message, {
    action: {
      label: "Undo",
      onClick: () => {
        void attempt(undo);
      }
    }
  });

/** Each entry's writes run in order, so a release never overtakes a save. */
const writes = new Map<string, Promise<unknown>>();

const queued = <T>(id: string, run: () => Promise<T>): Promise<T> => {
  const next = (writes.get(id) ?? Promise.resolve()).then(run);
  const settled = next.catch(() => undefined);
  writes.set(id, settled);
  void settled.then(() => {
    if (writes.get(id) === settled) writes.delete(id);
  });
  return next;
};

const settledWrites = (ids: string[]) =>
  Promise.all(ids.map((id) => writes.get(id)));

export const timeActions = {
  update: (id: string, patch: EntryPatch) =>
    queued(id, () => attempt(() => TimeAPI.update(id, patch))),

  create: async (body: NewEntry): Promise<TimeEntry | null> => {
    const entry = await attempt(() => TimeAPI.create(body));
    if (entry) {
      undoable(
        `Added ${formatHours(entry.duration_hours)}${
          entry.case_label ? ` to ${entry.case_label}` : ""
        }`,
        () => TimeAPI.discard([entry.id])
      );
    }
    return entry;
  },

  assign: async (id: string, individualId: string) => {
    const out = await attempt(() => TimeAPI.assign(id, individualId));
    if (out?.learned) {
      const pref = out.learned;
      undoable(`From now on, ${pref.label}`, () =>
        TimeAPI.deletePreference(pref.id)
      );
    }
    return out?.entry ?? null;
  },

  release: async (entries: Pick<TimeEntry, "id" | "duration_hours">[]) => {
    if (!entries.length) return null;
    const ids = entries.map((e) => e.id);
    flushDrafts(ids);
    await settledWrites(ids);
    const out = await attempt(() => TimeAPI.release(ids));
    if (out) {
      undoable(
        `Released ${plural(out.length, "entry", "entries")} · ${formatHours(
          totalHours(out)
        )}`,
        () => TimeAPI.recall(out.map((e) => e.id))
      );
    }
    return out;
  },

  recall: async (ids: string[]) => {
    const out = await attempt(() => TimeAPI.recall(ids));
    if (out) toast("Recalled. Only you can see it again.");
    return out;
  },

  discard: async (ids: string[]) => {
    const out = await attempt(() => TimeAPI.discard(ids));
    if (out) {
      undoable(
        out.length === 1
          ? "Entry discarded"
          : `${out.length} entries discarded`,
        () => TimeAPI.restore(ids)
      );
    }
    return out;
  },

  merge: async (ids: string[]) => {
    const out = await attempt(() => TimeAPI.merge(ids));
    if (out)
      undoable(
        `Merged ${ids.length} entries · ${formatHours(out.duration_hours)}`,
        () => TimeAPI.unmerge(out.id)
      );
    return out;
  },

  split: async (entry: Pick<TimeEntry, "id" | "duration_hours">) => {
    const first = Math.max(
      0.1,
      Math.round((entry.duration_hours / 2) * 10) / 10
    );
    const out = await attempt(() => TimeAPI.split(entry.id, first));
    if (out && out.length === 2) {
      undoable(
        `Split into ${formatHours(out[0].duration_hours)} and ${formatHours(
          out[1].duration_hours
        )}. Write the second narrative.`,
        () => TimeAPI.unsplit(out[0].id)
      );
    }
    return out;
  },

  startTimer: (individualId?: string | null) =>
    attempt(() => TimeAPI.startTimer(individualId)),

  stopTimer: async (discard = false) => {
    const out = await attempt(() => TimeAPI.stopTimer(discard));
    if (!out) return out;
    const { entry } = out;
    if (entry) {
      undoable(
        `Logged ${formatHours(entry.duration_hours)} from the timer${
          entry.case_label ? ` to ${entry.case_label}` : ". Pick its case."
        }`,
        () => TimeAPI.discard([entry.id])
      );
    } else {
      toast(
        discard ? "Timer discarded" : "Under a minute, so nothing was logged"
      );
    }
    return out;
  },

  /** Fee and billing IDs for a case; unexported time is repriced. */
  saveCaseBilling: (individualId: string, patch: Partial<TimeMatter>) =>
    attempt(() => TimeAPI.saveCaseBilling(individualId, patch))
};

/** Case expenses: anyone on the case adds and confirms; admins approve. */
export const expenseActions = {
  add: async (individualId: string, body: NewExpense) => {
    const out = await attempt(() => ExpenseAPI.create(individualId, body));
    if (out)
      undoable(
        `Added ${formatAmount(out.amount)} ${out.kind_label.toLowerCase()}`,
        () => ExpenseAPI.discard([out.id])
      );
    return out;
  },

  update: (id: string, patch: Partial<NewExpense>) =>
    attempt(() => ExpenseAPI.update(id, patch)),

  confirm: async (ids: string[]) => {
    const out = await attempt(() => ExpenseAPI.confirm(ids));
    if (out)
      toast(
        `Confirmed ${plural(out.length, "expense", "expenses")}. ${
          out.length === 1 ? "It goes" : "They go"
        } to billing review.`
      );
    return out;
  },

  discard: async (ids: string[]) => {
    const out = await attempt(() => ExpenseAPI.discard(ids));
    if (out)
      undoable(
        out.length === 1
          ? "Expense discarded"
          : `${out.length} expenses discarded`,
        () => ExpenseAPI.restore(ids)
      );
    return out;
  },

  approve: async (ids: string[]) => {
    const out = await attempt(() => ExpenseAPI.approve(ids));
    if (out)
      undoable(
        `Approved ${plural(out.length, "expense", "expenses")} · ${formatAmount(
          out.reduce((sum, x) => sum + x.amount, 0)
        )}`,
        () => ExpenseAPI.unapprove(out.map((x) => x.id))
      );
    return out;
  },

  unapprove: (ids: string[]) => attempt(() => ExpenseAPI.unapprove(ids))
};

/** Billing review: what firm admins do with released time. */
export const reviewActions = {
  approve: async (ids: string[], force = false) => {
    const out = await attempt(() => TimeAdminAPI.approve(ids, force));
    if (out) {
      undoable(
        `Approved ${plural(out.length, "entry", "entries")} · ${formatHours(
          totalHours(out)
        )}`,
        () => TimeAdminAPI.unapprove(out.map((e) => e.id))
      );
    }
    return out;
  },

  returnEntries: async (ids: string[], note: string) => {
    const out = await attempt(() => TimeAdminAPI.returnEntries(ids, note));
    if (out)
      undoable(
        `Returned ${plural(out.length, "entry", "entries")} with your note`,
        () => TimeAdminAPI.unreturn(out.map((e) => e.id))
      );
    return out;
  },

  bulkEdit: async (
    ids: string[],
    fields: Record<string, unknown>,
    note?: string
  ) => {
    const out = await attempt(() => TimeAdminAPI.bulkEdit(ids, fields, note));
    if (out) toast(`Updated ${plural(out.length, "entry", "entries")}`);
    return out;
  },

  saveTimekeeper: (userId: string, patch: Partial<Timekeeper>) =>
    attempt(() => TimeAdminAPI.saveTimekeeper(userId, patch)),

  saveCaseBilling: timeActions.saveCaseBilling,

  saveFirmLedesId: (id: string) =>
    attempt(() => TimeAdminAPI.saveSettings({ firm_ledes_id: id || null }))
};

const firstName = (name?: string) => (name || "the timekeeper").split(" ")[0];

/** Pre-bill: each flag action says what it did and undoes from the toast. */
export const prebillActions = {
  fix: async (flag: EntryFlag) => {
    const out = await attempt(() => PrebillAPI.flag(flag.id, "fix"));
    if (out)
      undoable(
        flag.fix?.kind === "reviewed"
          ? "Marked reviewed"
          : `${flag.fix?.label || "Fix applied"} · ${flag.label}`,
        () => PrebillAPI.flag(flag.id, "unfix")
      );
    return out;
  },

  dismiss: async (flag: EntryFlag, reason = "") => {
    const out = await attempt(() =>
      PrebillAPI.flag(flag.id, "dismiss", { reason })
    );
    if (out)
      undoable(`Dismissed · ${flag.label}`, () =>
        PrebillAPI.flag(flag.id, "undismiss")
      );
    return out;
  },

  returnEntry: async (flag: EntryFlag, toName?: string) => {
    const out = await attempt(() => PrebillAPI.flag(flag.id, "return"));
    if (out)
      undoable(
        `Returned to ${firstName(toName)} with the rule as the note`,
        () => TimeAdminAPI.unreturn([out.id])
      );
    return out;
  },

  approveMatter: async (matter: PrebillMatter) => {
    const out = await attempt(() => PrebillAPI.approve(matter.individual_id));
    if (out)
      undoable(
        `Approved ${matter.client_label || matter.label} · ${plural(
          out.length,
          "entry",
          "entries"
        )}`,
        () => TimeAdminAPI.unapprove(out.map((e) => e.id))
      );
    return out;
  },

  nudge: async (row: WaitingRow, range: { from?: string; to?: string }) => {
    const out = await attempt(() =>
      TimeAdminAPI.nudge(row.user_id, range.from, range.to)
    );
    if (out)
      toast(`Nudged ${row.name}. They'll see it on Home and in the tracker.`);
    return out;
  }
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const exportActions = {
  download: async (batch: ExportBatch) => {
    try {
      saveBlob(await TimeAdminAPI.downloadExport(batch.id), batch.filename);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't download the export"
      );
    }
  },

  create: async (req: ExportRequest) => {
    const batch = await attempt(() => TimeAdminAPI.createExport(req));
    if (batch) {
      const parts = [
        batch.entry_count ? plural(batch.entry_count, "entry", "entries") : "",
        batch.expense_count
          ? plural(batch.expense_count, "expense", "expenses")
          : ""
      ].filter(Boolean);
      const one = batch.entry_count + (batch.expense_count ?? 0) === 1;
      toast(
        `Exported ${parts.join(" and ") || "nothing"} and locked ${
          one ? "it" : "them"
        }`
      );
      void exportActions.download(batch);
    }
    return batch;
  },

  priceAtApprovedRate: async (entryIds: string[]) => {
    const changed = await attempt(() =>
      TimeAdminAPI.priceAtApprovedRate(entryIds)
    );
    if (changed)
      toast(
        `Priced ${plural(
          changed.length,
          "entry",
          "entries"
        )} at the approved rate`
      );
    return changed;
  },

  void: async (batch: ExportBatch) => {
    const out = await attempt(() => TimeAdminAPI.voidExport(batch.id));
    if (out)
      toast(
        `Voided ${batch.ref}. ${
          batch.expense_count
            ? "Its entries and expenses are"
            : plural(batch.entry_count, "entry is", "entries are")
        } editable again.`
      );
    return out;
  }
};

export const ruleActions = {
  saveFirmRules: async (
    rules: BillingRule[],
    before: BillingRule[],
    message: string
  ) => {
    const saved = await attempt(() => TimeAdminAPI.saveSettings({ rules }));
    if (saved)
      undoable(message, () => TimeAdminAPI.saveSettings({ rules: before }));
    return saved;
  },

  saveGuidelineRules: async (
    guideline: TimeGuideline,
    rules: BillingRule[],
    message: string
  ) => {
    const before = guideline.rules;
    const saved = await attempt(() =>
      GuidelineAPI.saveRules(guideline.id, rules)
    );
    if (saved)
      undoable(message, () => GuidelineAPI.saveRules(guideline.id, before));
    return saved;
  },

  setCaseRule: async (individualId: string, rule: AppliedRule, on: boolean) => {
    const out = await attempt(() =>
      GuidelineAPI.setCaseRule(individualId, rule.id, on)
    );
    if (out)
      undoable(
        `${on ? "Turned on" : "Turned off"} "${rule.label}" for this case`,
        () => GuidelineAPI.setCaseRule(individualId, rule.id, !on)
      );
    return out;
  }
};

export const guidelineActions = {
  upload: async (
    owner: GuidelineOwner,
    file: File,
    kind: GuidelineKind = "guidelines"
  ) => {
    const saved = await attempt(() => GuidelineAPI.upload(owner, file, kind));
    if (saved)
      toast(
        kind === "engagement_letter"
          ? `Saved ${saved.filename}`
          : saved.status === "extracting"
          ? `Reading the rules in ${saved.filename}`
          : `Read ${plural(saved.rule_count, "rule", "rules")} from ${
              saved.filename
            }`
      );
    return saved;
  },

  extract: async (guideline: TimeGuideline) => {
    const out = await attempt(() => GuidelineAPI.extract(guideline.id));
    if (out) toast(`Reading ${guideline.filename} again`);
    return out;
  },

  remove: async (guideline: TimeGuideline) => {
    const out = await attempt(() => GuidelineAPI.remove(guideline.id));
    if (out) toast(`Removed ${guideline.filename}`);
    return out;
  },

  open: async (guideline: TimeGuideline) => {
    try {
      const blob = await GuidelineAPI.file(guideline.id);
      const url = URL.createObjectURL(
        new Blob([blob], { type: guideline.content_type })
      );
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't open the file"
      );
    }
  }
};
